import { formatUkDateSafe, isSetDate } from "../../../Helpers/Dates";
import {
  AdditionalInfoFieldType,
  DDCell,
} from "../../../Types/AdditionalInformation/AdditionalInfoField";
import { IDropDownListItem } from "../../../Types/DropDownList/IDropDownListItem";
import { getProductTypeName, ProductType } from "../../../Types/ProductType";
import { IDynamicField } from "../../../Types/Products/IDynamicField";
import { ICriteriaItem } from "../../../Types/Products/ICriteriaItemData";
import { IProductQuestionnaire } from "../../../Types/Products/IProductQuestionnaire";
import { flattenRows } from "../../fieldTypeUtils";
import {
  AreaExportData,
  EXPORT_COLS,
  ExportFieldVisibility,
  ExportSheetColumnKey,
  fullExportFieldVisibility,
} from "../types";

/** Non-archived fields in the same order as the diagnostic page (API array order). */
export function fieldsInExportOrder(
  fields: IDynamicField[] | undefined
): IDynamicField[] {
  return (fields ?? []).filter((f) => !f.archived);
}

export function tagsString(c: ICriteriaItem): string {
  if (!c.tags?.length) return "";

  if (c.tagsData?.length) {
    return c.tags
      .map((tagId) => {
        const option = c.tagsData.find((o) => o.value === tagId);
        return option?.label ?? tagId;
      })
      .filter(Boolean)
      .join(", ");
  }

  return c.tags.join(", ");
}

export function findGradeOptionForStoredValue(
  q: IProductQuestionnaire,
  gradeRaw: unknown
): IDropDownListItem | undefined {
  const opts = q.gradeOptions ?? [];
  if (!opts.length) return undefined;
  if (gradeRaw === undefined || gradeRaw === null) return undefined;
  const g = String(gradeRaw).trim();
  if (!g.length || g === "Not Set" || g === "None") return undefined;
  return opts.find(
    (o) =>
      (o.value !== undefined && String(o.value) === g) || o.text === g
  );
}

/** Resolve a stored grade value to its display label via gradeOptions. */
function resolveGradeLabel(
  q: IProductQuestionnaire,
  gradeRaw: unknown
): string {
  const opt = findGradeOptionForStoredValue(q, gradeRaw);
  if (opt?.text?.trim()) return opt.text.trim();
  if (gradeRaw === undefined || gradeRaw === null) return "Not Set";
  const g = String(gradeRaw).trim();
  if (!g.length || g === "None") return "Not Set";
  return g;
}

/** Questionnaire overall grade for summary/header exports. */
export function gradeDisplay(q: IProductQuestionnaire): string {
  return resolveGradeLabel(q, q.overallGrade);
}

/** Criteria-level grade for tabular exports (one row per field within a criteria). */
export function criteriaGradeDisplay(
  q: IProductQuestionnaire,
  c: ICriteriaItem
): string {
  return resolveGradeLabel(q, c.grade);
}

/** Develop criteria-level Person values (subs → display names via personData). */
export function developPersonContent(c: ICriteriaItem): string {
  const raw = c.person;
  const subs = Array.isArray(raw) ? raw : raw ? [String(raw)] : [];
  if (subs.length === 0) return "";
  return subs
    .map((sub) => {
      const item = c.personData?.find((d) => String(d.value) === String(sub));
      return item?.text ?? sub;
    })
    .filter(Boolean)
    .join(", ");
}

export function developPicklistContent(c: ICriteriaItem): string {
  return (c.picklist ?? []).map((p) => p.label).filter(Boolean).join(", ");
}

export function developDropdownContent(c: ICriteriaItem): string {
  const v = c.dropdown?.trim() ?? "";
  if (!v) return "";
  const match = c.dropdownData?.find((d) => String(d.value) === String(v));
  return match?.text?.trim() ? match.text : v;
}

/** Trimmed introduction / scope text for an area. */
export function introductionText(q: IProductQuestionnaire): string {
  return (q.scope ?? "").trim();
}

export type AdditionalInfoEntry = { header: string; value: string };

function formatAdditionalInfoValue(cell: DDCell): string {
  switch (cell.type) {
    case AdditionalInfoFieldType.TextArea:
    case AdditionalInfoFieldType.DropDownList:
      return (cell.text ?? "").trim();
    case AdditionalInfoFieldType.DatePicker:
      return isSetDate(cell.date) ? formatUkDateSafe(cell.date) : "";
    case AdditionalInfoFieldType.TimePicker: {
      if (!isSetDate(cell.date)) return "";
      try {
        return new Date(cell.date).toISOString().slice(11, 16);
      } catch {
        return "";
      }
    }
    case AdditionalInfoFieldType.MultiSelect:
      return (cell.selection ?? []).filter(Boolean).join(", ");
    case AdditionalInfoFieldType.Switch:
      return cell.state ? "Yes" : "No";
    default:
      return (cell.text ?? "").trim();
  }
}

/** Flatten and format additional-information data boxes for export. */
export function additionalInfoEntries(
  q: IProductQuestionnaire
): AdditionalInfoEntry[] {
  const cells = flattenRows(q.dataBoxRows ?? []);
  const out: AdditionalInfoEntry[] = [];
  for (const cell of cells) {
    const header = (cell.header ?? "").trim();
    const value = formatAdditionalInfoValue(cell);
    if (!header && !value) continue;
    out.push({ header, value });
  }
  return out;
}

function rowFromValues(
  values: Record<ExportSheetColumnKey, string>,
  vis: ExportFieldVisibility
): string[] {
  return EXPORT_COLS.filter((k) => vis[k]).map((k) => values[k] ?? "");
}

export function buildExportHeader(vis: ExportFieldVisibility): string[] {
  return EXPORT_COLS.filter((k) => vis[k]);
}

/**
 * Data rows only (no header). Respects column visibility (same order as EXPORT_COLS).
 */
export function buildExportDataRows(
  areas: AreaExportData[],
  fieldVisibility?: Partial<ExportFieldVisibility>
): string[][] {
  const vis = fullExportFieldVisibility(fieldVisibility);
  const rows: string[][] = [];

  for (const area of areas) {
    const q = area.questionnaire;
    const templateType = getProductTypeName(q.productType);
    const due = formatUkDateSafe(area.displayInfo?.dueDate);
    const owner = area.displayInfo?.ownerName ?? "";
    const completionRaw = area.displayInfo?.completedDate;
    const completion = formatUkDateSafe(
      isSetDate(completionRaw) ? completionRaw : undefined
    );
    const isDevelop = q.productType === ProductType.Develop;
    const personTitle = (q.personName?.trim() || "Person") as string;
    const picklistTitle = (q.picklistName?.trim() || "Picklist") as string;
    const dropdownTitle = (q.dropdownName?.trim() || "Dropdown") as string;

    const pushFieldRow = (
      sectionTitle: string,
      criteriaTitle: string,
      fieldTitle: string,
      fieldContent: string,
      grade: string,
      tags: string,
      evidence: string
    ) => {
      const values: Record<ExportSheetColumnKey, string> = {
        Title: q.title ?? "",
        Grade: grade,
        "Due date": due,
        Owner: owner,
        "Type of template": templateType,
        "Completion date": completion,
        Section: sectionTitle,
        Criteria: criteriaTitle,
        "Field title": fieldTitle,
        "Field content": fieldContent,
        Tags: tags,
        "Linked evidence files": evidence,
      };
      rows.push(rowFromValues(values, vis));
    };

    const canEmitFieldRows = vis["Field title"] || vis["Field content"];

    if (vis.Introduction && canEmitFieldRows) {
      const intro = introductionText(q);
      if (intro) {
        pushFieldRow("Introduction", "", "Introduction", intro, "", "", "");
      }
    }

    if (vis["Additional information"] && canEmitFieldRows) {
      for (const entry of additionalInfoEntries(q)) {
        pushFieldRow(
          "Additional information",
          "",
          entry.header,
          entry.value,
          "",
          "",
          ""
        );
      }
    }

    for (const section of q.questionnaireSections ?? []) {
      for (const c of section.criteria ?? []) {
        const tags = tagsString(c);
        const grade = criteriaGradeDisplay(q, c);
        const evidence = (area.evidenceByCriteriaId[c.id] ?? []).join("; ");
        const fields = fieldsInExportOrder(c.fields);
        const sectionTitle = section.title ?? "";
        const criteriaTitle = c.title ?? "";

        if (isDevelop) {
          if (q.personShown) {
            pushFieldRow(
              sectionTitle,
              criteriaTitle,
              personTitle,
              developPersonContent(c),
              grade,
              tags,
              evidence
            );
          }
          if (q.dropdownShown) {
            pushFieldRow(
              sectionTitle,
              criteriaTitle,
              dropdownTitle,
              developDropdownContent(c),
              grade,
              tags,
              evidence
            );
          }
          if (q.picklistShown) {
            pushFieldRow(
              sectionTitle,
              criteriaTitle,
              picklistTitle,
              developPicklistContent(c),
              grade,
              tags,
              evidence
            );
          }
        }

        for (const f of fields) {
          const fieldTitle = f.title
            ? `${f.title}${f.mandatory ? " *" : ""}`
            : "";
          pushFieldRow(
            sectionTitle,
            criteriaTitle,
            fieldTitle,
            f.content ?? "",
            grade,
            tags,
            evidence
          );
        }
      }
    }
  }

  return rows;
}
